import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";

import { Cargando, EstadoVacio } from "../components/Estados";
import {
  asignarEjemplar,
  cargarEjemplares,
  obtenerEjemplares,
  registrarEjemplar,
  retirarEjemplar,
} from "../services/bibliotecaApi";
import { momento } from "../model/presentacion";
import {
  descargarPlantillaEjemplares,
  estudiantesSinEjemplar,
  leerPlantillaEjemplares,
} from "./plantillaEjemplares";

const SIN_ESTUDIANTE = "";

/** Select de los estudiantes de la solicitud. */
const SelectorEstudiante = ({ valor, estudiantes, onChange, etiqueta, deshabilitado }) => (
  <TextField
    select
    fullWidth
    label={etiqueta}
    value={valor}
    disabled={deshabilitado}
    onChange={({ target }) => onChange(target.value)}
  >
    <MenuItem value={SIN_ESTUDIANTE}>
      <em>Sin asignar</em>
    </MenuItem>
    {estudiantes.map(({ id, nombre, identificacion }) => (
      <MenuItem key={id} value={id}>
        {nombre} ({identificacion})
      </MenuItem>
    ))}
  </TextField>
);

SelectorEstudiante.propTypes = {
  valor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  estudiantes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  etiqueta: PropTypes.string,
  deshabilitado: PropTypes.bool,
};

SelectorEstudiante.defaultProps = { etiqueta: "Prestado a", deshabilitado: false };

/**
 * Los libros fisicos que biblioteca lleva a un Libro a tu clase, como en
 * AGIL: cada uno con su codigo de barras y el estudiante al que queda
 * prestado. Para preparar la solicitud todos deben estar asignados.
 *
 * Se registran uno por uno o, para no hacerlo estudiante por estudiante, con
 * la plantilla de Excel: se descarga con los estudiantes que aun no tienen
 * libro, se llena el codigo de barras y el titulo, y se carga.
 *
 * Se manejan hasta que se entrega; despues quedan de consulta. Cada cambio
 * responde el estado completo, asi que la lista es siempre la del backend.
 */
const PanelEjemplares = ({ solicitud, onCambio, onMensaje }) => {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState("");
  const [codigo, setCodigo] = useState("");
  const [titulo, setTitulo] = useState("");
  const [estudiante, setEstudiante] = useState(SIN_ESTUDIANTE);
  const [retirando, setRetirando] = useState(null);
  const [nota, setNota] = useState("");
  const [enviando, setEnviando] = useState(false);
  // Lo que hay que corregir en el Excel cargado, si algo.
  const [erroresExcel, setErroresExcel] = useState([]);
  const archivo = useRef(null);

  useEffect(() => {
    let vigente = true;
    obtenerEjemplares(solicitud.id)
      .then((respuesta) => vigente && setDatos(respuesta))
      .catch((error) => vigente && setFallo(error.message));
    return () => {
      vigente = false;
    };
    // Se consulta una vez al abrir; despues se actualiza con cada respuesta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitud.id]);

  const aplicar = async (peticion, exito) => {
    setEnviando(true);
    const { ok, titulo: mensaje, datos: nuevos } = await peticion;
    setEnviando(false);
    onMensaje(ok ? exito : mensaje, ok ? "success" : "info");
    if (!ok) return false;
    setDatos(nuevos);
    onCambio();
    return true;
  };

  const registrar = async () => {
    const ok = await aplicar(
      registrarEjemplar(solicitud.id, { codigoBarras: codigo.trim(), titulo: titulo.trim(), estudiante }),
      "Ejemplar registrado.",
    );
    if (ok) {
      setCodigo("");
      setEstudiante(SIN_ESTUDIANTE);
      // El titulo se deja: suelen llegar varios ejemplares del mismo libro.
    }
  };

  const descargarPlantilla = () => {
    const pendientes = estudiantesSinEjemplar(datos.estudiantes, datos.ejemplares);
    if (pendientes.length === 0) {
      onMensaje("Todos los estudiantes ya tienen un ejemplar.", "info");
      return;
    }
    descargarPlantillaEjemplares(solicitud.id, pendientes, datos.tematicas);
  };

  const cargarExcel = async ({ target }) => {
    const elegido = target.files?.[0];
    // Se limpia para poder volver a elegir el mismo archivo ya corregido.
    target.value = "";
    if (!elegido) return;
    setErroresExcel([]);
    const { ejemplares: leidos, errores } = await leerPlantillaEjemplares(elegido, datos.estudiantes);
    if (errores.length) {
      setErroresExcel(errores);
      return;
    }
    setEnviando(true);
    const { ok, titulo: mensaje, datos: nuevos } = await cargarEjemplares(solicitud.id, leidos);
    setEnviando(false);
    if (!ok) {
      setErroresExcel(nuevos?.errores ?? [mensaje]);
      return;
    }
    onMensaje(
      `${leidos.length} ${leidos.length === 1 ? "ejemplar registrado" : "ejemplares registrados"}.`,
      "success",
    );
    setDatos(nuevos);
    onCambio();
  };

  const retirar = async () => {
    const ok = await aplicar(
      retirarEjemplar(solicitud.id, retirando.id, nota.trim()),
      "Ejemplar retirado.",
    );
    if (ok) {
      setRetirando(null);
      setNota("");
    }
  };

  if (fallo) return <EstadoVacio mensaje={fallo} />;
  if (!datos) return <Cargando />;

  const { maneja_ejemplares: maneja, ejemplares, retirados, estudiantes, tematicas } = datos;
  const sinAsignar = ejemplares.filter(({ estudiante: asignado }) => !asignado).length;

  return (
    <div>
      {maneja && (
        <>
          <Grid container spacing={1} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="subtitle2">Registrar ejemplar</Typography>
            </Grid>
            <Grid item>
              <Button size="small" startIcon={<GetAppIcon />} onClick={descargarPlantilla}>
                Descargar plantilla
              </Button>
              <Button
                size="small"
                color="primary"
                startIcon={<PublishIcon />}
                disabled={enviando}
                onClick={() => archivo.current?.click()}
              >
                Cargar Excel
              </Button>
              <input
                ref={archivo}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={cargarExcel}
              />
            </Grid>
          </Grid>
          {erroresExcel.length > 0 && (
            <Alert severity="error" onClose={() => setErroresExcel([])} style={{ margin: "8px 0" }}>
              No se registró ningún ejemplar. Corrige el archivo y vuelve a cargarlo:
              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                {erroresExcel.slice(0, 10).map((error) => (
                  <li key={error}>{error}</li>
                ))}
                {erroresExcel.length > 10 && <li>Y {erroresExcel.length - 10} más.</li>}
              </ul>
            </Alert>
          )}
          <Grid container spacing={1} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                autoFocus
                label="Código de barras"
                value={codigo}
                onChange={({ target }) => setCodigo(target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* Libre, pero sugiere lo que pidio el docente. */}
              <Autocomplete
                freeSolo
                options={tematicas}
                inputValue={titulo}
                onInputChange={(_evento, valor) => setTitulo(valor)}
                renderInput={(params) => <TextField {...params} label="Título" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <SelectorEstudiante
                valor={estudiante}
                estudiantes={estudiantes}
                onChange={setEstudiante}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                color="primary"
                disabled={!codigo.trim() || !titulo.trim() || enviando}
                onClick={registrar}
              >
                Registrar
              </Button>
            </Grid>
          </Grid>
        </>
      )}

      <Typography variant="subtitle2" style={{ marginTop: maneja ? 24 : 0 }}>
        Ejemplares ({ejemplares.length})
      </Typography>
      {sinAsignar > 0 && (
        <Alert severity="warning" style={{ margin: "8px 0" }}>
          {sinAsignar} de {ejemplares.length} sin estudiante. Para preparar la
          solicitud todos deben estar asignados.
        </Alert>
      )}
      {ejemplares.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          Aún no hay ejemplares registrados.
        </Typography>
      ) : (
        <List dense disablePadding>
          {ejemplares.map((ejemplar) => (
            <ListItem key={ejemplar.id} disableGutters divider>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <ListItemText
                    primary={ejemplar.titulo}
                    secondary={`Código ${ejemplar.codigo_barras}`}
                  />
                </Grid>
                <Grid item xs={10} sm={6}>
                  {maneja ? (
                    <SelectorEstudiante
                      valor={ejemplar.estudiante?.id ?? SIN_ESTUDIANTE}
                      estudiantes={estudiantes}
                      deshabilitado={enviando}
                      onChange={(nuevo) =>
                        nuevo &&
                        aplicar(asignarEjemplar(solicitud.id, ejemplar.id, nuevo), "Ejemplar asignado.")
                      }
                    />
                  ) : (
                    <Typography variant="body2">
                      {ejemplar.estudiante ? `Prestado a ${ejemplar.estudiante.nombre}` : "Sin asignar"}
                    </Typography>
                  )}
                </Grid>
                {maneja && (
                  <Grid item xs={2} sm={1}>
                    <Tooltip title="Retirar ejemplar">
                      <IconButton size="small" onClick={() => setRetirando(ejemplar)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </ListItem>
          ))}
        </List>
      )}

      {retirando && (
        <Grid container spacing={1} alignItems="flex-end" style={{ marginTop: 8 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              required
              label={`Por qué se retira ${retirando.codigo_barras}`}
              helperText="Queda en el detalle de la solicitud."
              value={nota}
              onChange={({ target }) => setNota(target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button onClick={() => setRetirando(null)}>No</Button>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button color="secondary" disabled={!nota.trim() || enviando} onClick={retirar}>
              Retirar
            </Button>
          </Grid>
        </Grid>
      )}

      {!maneja && (
        <Alert severity="info" style={{ marginTop: 16 }}>
          Los ejemplares solo se cambian antes de entregar, con permiso sobre ese
          estado o siendo auxiliar de la solicitud.
        </Alert>
      )}

      {retirados.length > 0 && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 24 }}>
            Retirados
          </Typography>
          <List dense disablePadding>
            {retirados.map((retirado) => (
              <ListItem key={retirado.id} disableGutters>
                <ListItemText
                  primary={`${retirado.titulo} · Código ${retirado.codigo_barras}`}
                  secondary={[
                    momento(retirado.fecha_elimina),
                    retirado.retirado_por && `por ${retirado.retirado_por}`,
                    retirado.nota_retiro,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );
};

PanelEjemplares.propTypes = {
  solicitud: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  /** Se llama tras cada cambio, para refrescar el detalle de fondo. */
  onCambio: PropTypes.func.isRequired,
  onMensaje: PropTypes.func.isRequired,
};

export default PanelEjemplares;
